/**
 * A GGUF file's tensors, in the same shape safetensors produces.
 *
 * `Inventory` is format-agnostic already, so a reader that fills it inherits the
 * tensor list, the byte ledger, compare mode, path URLs, the cache and share
 * cards without any of them learning that GGUF exists. That is the whole reason
 * phase one is cheap; the new logic is here and in `ggml-types.ts`.
 *
 * ## Two accounts of every tensor's size, and they are checked against each other
 *
 * A tensor's extent can be derived twice: from the **type table**
 * (`ceil(numel / blockSize) * typeSize`) and from the **layout** (the gap to the
 * next tensor's offset). Neither is redundant.
 *
 * The layout works for types we have no definition for, which is not a corner
 * case — 498 of 851 tensors in one real repo are ggml type 42, and its sibling
 * file uses type 142. The table is exact where it applies, with no alignment
 * padding folded in.
 *
 * Comparing them is what caught the type table being wrong. The ids from 25 up
 * were transcribed one out, so `BF16` was read as `IQ1_M` — two bytes per
 * element decoded as a 256-element block — and *nothing in the table could have
 * revealed it*. The layout disagreed on 50 of 79 tensors in a file named
 * `…-bf16.gguf`, which is how it was found. So the comparison stays, and a
 * surviving disagreement is reported on the tensor rather than absorbed.
 *
 * ## Sizes are stated for unpublished types, and that is a decision
 *
 * Reviewer ruling, 2026-08-09, after the measurement below overturned the
 * premise of the previous one: **all numeric views render for an unknown type.**
 * `ne[]` is a format-level field, so element count is present whatever the type
 * is; the layout supplies bytes; bits-per-weight is arithmetic on the two. Over
 * all 498 unknown-type tensors in `Ternary-Bonsai-27B-Q2_0.gguf` that comes to
 * exactly 2.1250 — 68 bytes per 256 elements — per tensor and in total. An exact
 * constant across 498 tensors is not what a misread field produces.
 *
 * What an unpublished type actually withholds is its **name** and its **decode**,
 * not its arithmetic. Blanking a cell whose number we can state exactly would be
 * the confidently-wrong error's mirror image: confidently withholding.
 *
 * The measured geometry is therefore reported *as measurement, never as format*
 * — "measured 68 bytes per 256 elements, format undocumented" — because naming a
 * format the publisher has not named is precisely the guess this project refuses.
 */

import { fetchRangeWithTotal, fileUrl } from "../hf/hub.js";
import type { ResolvedRepo } from "../hf/hub.js";
import type { InventoryTensor } from "../safetensors/inventory.js";
import { ggmlByteLength, ggmlTypeName, isKnownGgmlType } from "./ggml-types.js";
import { type GgufTensorInfo, NeedMoreBytes, parseGgufHeader } from "./header.js";

/**
 * First ask. Small enough that a 0.30 MiB header costs one request, large enough
 * that it is not obviously wasteful on the 10 MiB median.
 */
const FIRST_READ = 512 * 1024;

/** Ceiling on a single ask, so one pathological file cannot request a gigabyte. */
const MAX_READ = 64 * 1024 * 1024;

export interface GgufTensor extends InventoryTensor {
  readonly typeId: number;
  /**
   * True when the extent came from the layout rather than the type table.
   *
   * Which means it may include up to `alignment - 1` bytes of padding. See
   * `approximate` for whether that can actually move a displayed digit.
   */
  readonly sizeFromLayout: boolean;
  /**
   * True only when the padding bound is large enough to matter at display
   * precision. Almost never: the bound is `alignment / bytes`, and a megabyte
   * tensor against a 32-byte alignment is orders below any digit shown.
   */
  readonly approximate: boolean;
  /** Set when the two accounts of this tensor's size disagreed. */
  readonly sizeDisagreement?: string | undefined;
}

export interface GgufFileInventory {
  readonly tensors: readonly GgufTensor[];
  readonly metadata: ReadonlyMap<string, unknown>;
  /** Real bytes read to obtain the index — what the status line reports. */
  readonly headerBytesRead: number;
  readonly requests: number;
  readonly fileBytes: number | undefined;
}

/** Progress, so a multi-megabyte header can say so while it happens. */
export type GgufProgress = (bytesRead: number) => void;

/**
 * The largest relative error alignment padding can introduce before it is worth
 * flagging. Bits-per-weight is shown to two decimals, so a tenth of the last
 * displayed digit is comfortably below "could move what you see".
 */
const PADDING_TOLERANCE = 0.0005;

/** `type42`, `type142` — what `ggmlTypeName` emits for an unpublished id. */
const UNPUBLISHED_DTYPE = /^type\d+$/;

/**
 * Read one GGUF file's header and index.
 *
 * Ranged throughout. The parse is resumable — see `header.ts` — because the
 * format states no header length, so this asks, parses, and asks again for
 * exactly what the parser said it could not reach.
 */
export async function readGgufFile(
  repo: ResolvedRepo,
  path: string,
  onProgress?: GgufProgress,
): Promise<GgufFileInventory> {
  const url = fileUrl(repo, path);
  let bytes = new ArrayBuffer(0);
  let fileBytes: number | undefined;
  let requests = 0;
  let want = FIRST_READ;

  for (;;) {
    const result = await fetchRangeWithTotal(url, bytes.byteLength, want);
    requests++;
    fileBytes ??= result.totalBytes;
    bytes = concat(bytes, result.bytes);
    onProgress?.(bytes.byteLength);

    try {
      const header = parseGgufHeader(bytes);
      return {
        tensors: toInventoryTensors(
          header.tensors,
          path,
          header.dataStart,
          header.alignment,
          fileBytes,
        ),
        metadata: header.metadata,
        headerBytesRead: bytes.byteLength,
        requests,
        fileBytes,
      };
    } catch (error) {
      if (!(error instanceof NeedMoreBytes)) throw error;
      /*
       * Ask for what the parser said it needed, and at least a doubling. The
       * doubling matters more than it looks: a 5 MB token array is walked one
       * length-prefix at a time, and asking for exactly `needed` each time would
       * mean a request per string.
       */
      want = Math.min(
        Math.max(error.needed, bytes.byteLength * 2),
        bytes.byteLength + MAX_READ,
      );
      if (fileBytes !== undefined && want > fileBytes) want = fileBytes;
      if (want <= bytes.byteLength) {
        throw new Error(
          `GGUF header for ${path} did not end before the file did — the index is unreadable.`,
        );
      }
    }
  }
}

function concat(a: ArrayBuffer, b: ArrayBuffer): ArrayBuffer {
  const out = new Uint8Array(a.byteLength + b.byteLength);
  out.set(new Uint8Array(a), 0);
  out.set(new Uint8Array(b), a.byteLength);
  return out.buffer;
}

/**
 * Turn parsed tensor infos into inventory entries, sizing each one.
 *
 * Ordered by offset first, because the layout account is only meaningful in
 * offset order and the index is not required to be sorted.
 *
 * Exported because the size-disagreement rule below is the whole of what a
 * reader can be wrong about here, and the only way to test it without a real
 * multi-hundred-megabyte GGUF is to hand it infos directly.
 */
export function toInventoryTensors(
  infos: readonly GgufTensorInfo[],
  file: string,
  dataStart: number,
  alignment: number,
  fileBytes: number | undefined,
): GgufTensor[] {
  const byOffset = [...infos].sort((a, b) => a.offset - b.offset);
  const out: GgufTensor[] = [];

  for (let i = 0; i < byOffset.length; i++) {
    const info = byOffset[i] as GgufTensorInfo;
    const next = byOffset[i + 1];

    /*
     * The layout extent. For the last tensor there is no next offset, so it is
     * the file's own size minus where the tensor starts — and that size came off
     * the `Content-Range` of a request already made, not from a second one.
     */
    const layoutExtent =
      next !== undefined
        ? next.offset - info.offset
        : fileBytes !== undefined
          ? fileBytes - (dataStart + info.offset)
          : undefined;

    const fromTable = ggmlByteLength(info.typeId, info.numel);

    let byteLength: number;
    let sizeFromLayout: boolean;
    let sizeDisagreement: string | undefined;

    if (fromTable !== undefined) {
      /*
       * The cross-check. The layout includes padding to the next tensor, so it
       * may legitimately exceed the table figure by up to `alignment - 1`;
       * anything beyond that is the two accounts genuinely disagreeing.
       */
      const padded = Math.ceil(fromTable / alignment) * alignment;
      const disagrees =
        layoutExtent !== undefined &&
        layoutExtent !== fromTable &&
        layoutExtent !== padded;

      if (disagrees) {
        /*
         * **The file wins.** Plan §24's rule, which this branch used to state
         * and then contradict: the *number* is always geometry, because bytes
         * are ground truth; the *name* is the declared id, checked against
         * geometry; a disagreement is surfaced, not silently resolved. It was
         * surfaced — and then resolved in the table's favour anyway, which is
         * the half that was wrong.
         *
         * It cost something real. `darkstarinitiative`'s and
         * `prism-ml`'s Ternary-Bonsai files store 498 tensors of ggml type
         * **42** at 2.125 bits per weight — 68 bytes per 256 elements — and
         * this module's own header records that measurement. Upstream ggml
         * *also* defines id 42, as `Q2_0` at `(64, 18)` = 2.25 bpw, which is
         * what `GGML_TYPES` correctly carries. Both are true: the id collides
         * between upstream and a publisher's own format.
         *
         * With the table winning, those tensors were sized at 72 bytes per
         * block against an actual 68 — so `dataOffsets` over-read four bytes a
         * block into the *next* tensor. Before the id-42 row existed the
         * layout answered and was right; adding a correct row broke a working
         * case, which is the shape of regression a table can cause just by
         * gaining an entry.
         *
         * Preferring the layout fixes every such collision at once rather than
         * this one, and it needs no ruling on whose name is right.
         */
        byteLength = layoutExtent as number;
        sizeFromLayout = true;
        sizeDisagreement = `${ggmlTypeName(info.typeId)} says ${fromTable} B, the layout says ${layoutExtent} B — sized from the layout`;
      } else {
        byteLength = fromTable;
        sizeFromLayout = false;
      }
    } else if (layoutExtent !== undefined && layoutExtent > 0) {
      byteLength = layoutExtent;
      sizeFromLayout = true;
    } else {
      byteLength = 0;
      sizeFromLayout = true;
    }

    /*
     * Whether padding could move a digit the reader sees. `alignment / bytes` is
     * the worst-case relative error, so this is arithmetic rather than judgement,
     * it is free, and on real tensors it never fires — a 337 MB tensor against a
     * 32-byte alignment is seven orders below two decimal places.
     */
    const approximate =
      sizeFromLayout && byteLength > 0 && alignment / byteLength > PADDING_TOLERANCE;

    const absoluteStart = dataStart + info.offset;
    out.push({
      name: info.name,
      dtype: ggmlTypeName(info.typeId),
      shape: info.shape,
      numel: info.numel,
      byteLength,
      dataOffsets: [info.offset, info.offset + byteLength],
      file,
      absoluteStart,
      absoluteEnd: absoluteStart + byteLength,
      typeId: info.typeId,
      sizeFromLayout,
      approximate,
      sizeDisagreement,
    });
  }
  return out;
}

/**
 * What to disclose about unpublished types, or `undefined` when there are none.
 *
 * Reviewer ruling (2026-08-09): every numeric view renders for an unknown type,
 * because `ne[]` is a format-level field and the layout supplies bytes — so
 * params, bytes and bits-per-weight are all arithmetic on facts the file
 * states. What an unpublished type withholds is its **name** and its **decode**.
 * Blanking a cell whose number we can state exactly would be the
 * confidently-wrong error's mirror image: confidently withholding.
 */
export function describeUnknownTypes(
  tensors: readonly {
    readonly dtype: string;
    readonly numel: number;
    readonly byteLength: number;
  }[],
): string | undefined {
  /*
   * Detected from the dtype string rather than from a type id, so this works on
   * a plain `Inventory` and the UI needs no GGUF-specific type. `ggmlTypeName`
   * emits exactly `type<id>` for anything unpublished, and nothing else in the
   * codebase produces that shape — safetensors dtypes are `F32`, `BF16`, `U8`.
   */
  const unknown = tensors.filter((t) => UNPUBLISHED_DTYPE.test(t.dtype));
  if (unknown.length === 0) return undefined;

  const ids = [...new Set(unknown.map((t) => t.dtype.slice(4)))].sort(
    (a, b) => Number(a) - Number(b),
  );
  const params = unknown.reduce((n, t) => n + t.numel, 0);
  const bytes = unknown.reduce((n, t) => n + t.byteLength, 0);
  const label = `${unknown.length.toLocaleString()} of ${tensors.length.toLocaleString()} tensors an unpublished type (${ids.join(", ")})`;
  if (params === 0 || bytes === 0) return `${label} — format undocumented`;

  const bpw = (bytes * 8) / params;
  /*
   * The measured geometry is stated only when it is genuinely constant across
   * every tensor using the type. A ratio that varies is a per-file average and
   * says nothing about a format; an exact constant over hundreds of tensors is
   * evidence that both `ne[]` and the offsets were read correctly, and the
   * reader deserves it (reviewer, 2026-08-09).
   *
   * Stated as measurement, never as format. Naming the format those 68 bytes
   * imply would be the guess this project exists not to make.
   */
  const perTensor = new Set(
    unknown
      .filter((t) => t.numel > 0)
      .map((t) => ((t.byteLength * 8) / t.numel).toFixed(6)),
  );
  const bytesPerBlock = (bpw * 256) / 8;
  const geometry =
    perTensor.size === 1 && Number.isInteger(bytesPerBlock)
      ? `measured ${bytesPerBlock} bytes per 256 elements, `
      : "";
  const width = bpw.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
  return `${label} — ${geometry}${width} bits/weight, format undocumented`;
}
